import React, { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Select } from "@mui/material";
import {
  tokens,
  SignalBar,
  StatusPill,
  CodeChip,
  ClinicalDataGrid,
  ClinicalPageShell,
} from "../../shared/clinical-design";
import DischargeDetailPanel from "../components/DischargeDetailPanel";
import { getDischargeWorkQueue } from "../services/postDischargeService";
import {
  LOCATIONS,
  DEFAULT_LOCATION,
  FOLLOW_UP_META,
  FOLLOW_UP_STATUS,
  TCM_WINDOW,
} from "../common/constants";

const COLUMNS = [
  {
    key: "name",
    header: "Patient",
    render: (r) => r.name,
  },
  {
    key: "laceScore",
    header: "LACE+",
    align: "right",
    mono: true,
    width: 70,
    render: (r) => (
      <CodeChip
        code={String(r.laceScore ?? "—")}
        tone={(r.laceScore ?? 0) >= 13 ? "crit" : (r.laceScore ?? 0) >= 10 ? "warn" : "ok"}
      />
    ),
  },
  {
    key: "daysSinceDischarge",
    header: "Days out",
    align: "right",
    mono: true,
    width: 80,
  },
  {
    key: "facility",
    header: "Facility",
    render: (r) => (
      <Box component="span" sx={{ fontSize: 12.5, color: tokens.ink2 }}>
        {r.facility || "Unknown"}
      </Box>
    ),
  },
  {
    key: "followUpStatus",
    header: "Follow-up",
    render: (r) => {
      const meta = FOLLOW_UP_META[r.followUpStatus] || FOLLOW_UP_META.PENDING;
      return <StatusPill tone={meta.tone} label={meta.label} dense />;
    },
  },
];

/**
 * Module A — Transition Board (Post-Discharge Care Coordination).
 * Master-detail: cola priorizada por LACE+ a la izquierda, detalle del alta
 * con closed-loop tracker y ventanas TCM a la derecha.
 */
export default function PostDischargePage() {
  const [locationKey, setLocationKey] = useState(DEFAULT_LOCATION);
  const [data, setData] = useState({ stats: {}, discharges: [] });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getDischargeWorkQueue(locationKey)
      .then((d) => {
        if (!active) return;
        setData(d);
        setSelectedId(d.discharges[0]?.cipId ?? null);
      })
      .catch(() => {
        if (!active) return;
        setData({ stats: {}, discharges: [] });
        setSelectedId(null);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [locationKey]);

  const selectedRow = useMemo(
    () => data.discharges.find((d) => d.cipId === selectedId) || null,
    [data, selectedId]
  );

  const signals = useMemo(() => {
    const rows = data.discharges;
    const total = rows.length;
    const scheduledOrDone = rows.filter((r) =>
      [FOLLOW_UP_STATUS.SCHEDULED, FOLLOW_UP_STATUS.COMPLETED].includes(r.followUpStatus)
    ).length;
    const tcm7 = rows.filter((r) => r.tcmWindow === TCM_WINDOW.WINDOW_7D).length;
    const expired = rows.filter((r) => r.tcmWindow === TCM_WINDOW.EXPIRED).length;
    const pct = total ? Math.round((scheduledOrDone / total) * 100) : 0;
    return [
      { label: "Active discharges", value: total, tone: "info" },
      {
        label: "Avg LACE+",
        value: data.stats.avgLaceScore ?? "—",
        hint: "Average LACE+ readmission risk score across the active queue",
      },
      {
        label: "High LACE (≥13)",
        value: data.stats.highLaceCount ?? 0,
        tone: (data.stats.highLaceCount ?? 0) > 0 ? "crit" : "ok",
      },
      {
        label: "TCM 7-day open",
        value: tcm7,
        tone: tcm7 > 0 ? "warn" : "muted",
        hint: "Discharges still inside the 7-day TCM window (CPT 99496 eligible)",
      },
      { label: "TCM expired", value: expired, tone: expired > 0 ? "crit" : "ok" },
      {
        label: "Scheduled / done",
        value: `${pct}%`,
        tone: pct >= 80 ? "ok" : pct >= 50 ? "warn" : "crit",
        hint: "Share of discharges with a follow-up appointment scheduled or completed",
      },
    ];
  }, [data]);

  const locationLabel =
    LOCATIONS.find((l) => l.key === locationKey)?.label || locationKey;

  return (
    <ClinicalPageShell
      title="Transition Board"
      subtitle="Post-discharge follow-up queue, prioritized by LACE+ readmission risk. Close the loop: discharge → outreach → appointment → completed visit."
      moduleTag="Module A"
      devPreview
      actions={
        <Select
          size="small"
          value={locationKey}
          onChange={(e) => setLocationKey(e.target.value)}
          sx={{ fontSize: 13, minWidth: 170, bgcolor: tokens.surface }}
        >
          {LOCATIONS.map((l) => (
            <MenuItem key={l.key} value={l.key} sx={{ fontSize: 13 }}>
              {l.label}
            </MenuItem>
          ))}
        </Select>
      }
    >
      <SignalBar signals={signals} sx={{ mb: 1.5 }} />
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "flex-start",
          flexDirection: { xs: "column", lg: "row" },
        }}
      >
        <Box sx={{ width: { xs: "100%", lg: "45%" }, flexShrink: 0 }}>
          <ClinicalDataGrid
            columns={COLUMNS}
            rows={data.discharges}
            rowKey={(r) => r.cipId}
            loading={loading}
            onRowClick={(r) => setSelectedId(r.cipId)}
            isRowSelected={(r) => r.cipId === selectedId}
            emptyMessage="No recent discharges for this location."
            maxHeight={620}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <DischargeDetailPanel
            key={selectedRow?.cipId ?? "none"}
            row={selectedRow}
            locationLabel={locationLabel}
          />
        </Box>
      </Box>
    </ClinicalPageShell>
  );
}
